import React, { useMemo, useRef } from 'react';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as util from '../../utils/util';
import { getCarePlan } from '../../utils/carePlan';

import {
  Box,
  DISEASE_CARD_DEFS,
  DigitalTwinShell,
  ORGAN_ITEMS,
  ORGAN_MODEL_MAP,
  SectionCard,
} from './DigitalTwinShared';
import {
  DiseaseApiKey,
  findOrganRisk,
  organGradeLabel,
  resolveDiseaseBadge,
  toRiskSummary,
  useDigitalTwin,
} from './digitalTwinApi';

// AI 위험도 분석 아이콘 (src/assets/icon/ 에 파일 저장 후 사용)
import iconAiStress from '../../assets/icon/iconAiStress.png';
import iconAiCholesterol from '../../assets/icon/iconAiCholesterol.png';
import iconAiExercise from '../../assets/icon/iconAiExercise.png';

/**
 * 화면 3: 예측 위험도 - 질환별 상세 분석
 * 화면 2 → 게이지 카드 "상세분석 >" 클릭 시 진입
 */
/** 상태 문구 → 색 (양호/주의/나쁨) */
const STATUS_TONE: Record<string, string> = {
  '양호': '#35AE17',
  '주의': '#ff8000',
  '나쁨': '#ed5454',
};

/** 케어 화면 경로 — 홈(MainCarePlan)·케어 홈(Care.tsx)이 쓰는 것과 같다 */
const CARE_DIET_PATH = '/care/CareMenu';     // 맞춤식단
const CARE_SPORTS_PATH = '/care/CareSports'; // 맞춤운동
/** 맞춤플랜이 아직 없을 때 보내는 설정 화면 */
const CARE_START_PATH = '/care/CareStep';

const DigitalTwinScreen3 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: twin } = useDigitalTwin();

  /**
   * 맞춤식단·맞춤운동으로 이동. 케어 홈(Care.tsx)·홈(MainCarePlan)과 같은 순서로 판정한다:
   *   미로그인 → 로그인 / 맞춤플랜 없음 → 설정(CareStep) / 있음 → 요청한 화면.
   *
   * 플랜 유무를 보지 않고 CareMenu·CareSports 로 바로 보내면 안 된다 — 그 화면들은
   * 플랜이 없으면 careStore 의 값으로 AP_004_003(플랜 생성)을 호출하는데, 설정을
   * 한 번도 안 한 계정은 그 값이 신장·체중 0 이라 0 짜리 플랜이 만들어진다.
   *
   * getCarePlan 은 모듈 캐시라 화면 2 를 거쳐 왔다면 추가 요청 없이 바로 돌아온다.
   */
  const careBusyRef = useRef(false);
  const goCarePlan = (target: string) => {
    if (util.strNull(window.localStorage.userCusID) === '') {
      navigate('/login');
      return;
    }
    if (careBusyRef.current) return; // 응답 오기 전 연타 방지
    careBusyRef.current = true;
    getCarePlan()
      .then((plan) => {
        navigate(plan.exists ? target : CARE_START_PATH);
      })
      .catch((error) => {
        // 조회 실패를 '플랜 없음'으로 단정하지 않는다 — 케어 홈이 같은 판정을 다시 해 준다
        console.log(error);
        navigate('/care');
      })
      .finally(() => {
        careBusyRef.current = false;
      });
  };

  /*
   * 이 화면이 무엇의 상세인지는 쿼리로 정해진다.
   *   ?organ=heart    → 장기 상세 (화면 2 의 '상세분석 ›')
   *   ?disease=diabetes → 질환 상세
   * 장기 판단에는 근거·부족 데이터와 해석이 없어(스펙상 질환에만 있다)
   * 해당 구간은 값이 있을 때만 그린다.
   */
  const organKey = searchParams.get('organ');
  const diseaseKey = searchParams.get('disease') as DiseaseApiKey | null;

  const view = useMemo(() => {
    if (diseaseKey && twin?.disease?.[diseaseKey]) {
      const d = twin.disease[diseaseKey];
      return {
        title: d.diseaseName,
        // 질환 아이콘 — 화면 1·2 질환 카드와 같은 아이콘(DISEASE_CARD_DEFS)을 쓴다
        icon: DISEASE_CARD_DEFS.find((def) => def.key === diseaseKey)?.icon,
        grade: d.riskGrade,
        // 화면 1·2 의 질환 카드와 같은 환산기를 쓴다 — 여기서 따로 판정하면
        // 카드는 '나쁨', 이 화면은 '주의' 처럼 같은 질환이 화면마다 다르게 보인다.
        status: resolveDiseaseBadge(d.riskGrade).badge,
        summary: toRiskSummary(d),
        evidence: d.evidenceData ?? [],
        missing: d.missingData ?? [],
        interpretation: d.interpretation ?? '',
        // 서버가 이 사용자에 맞춰 생성한 설명문 (AP_009_002)
        explainSummary: d.diseaseSummary ?? '',
        explainDetail: d.detailedAnalysis ?? '',
      };
    }
    if (organKey) {
      const o = findOrganRisk(twin, organKey);
      const status = organGradeLabel(o);
      return {
        title: ORGAN_MODEL_MAP[organKey]?.label ?? o?.organName ?? '장기',
        // 장기 아이콘 — 장기 목록(ORGAN_ITEMS)의 아이콘을 쓴다
        icon: ORGAN_ITEMS.find((item) => item.value === organKey)?.icon,
        grade: o?.riskGrade ?? '미측정',
        status,
        summary: toRiskSummary(o),
        // 장기 응답에는 아래 3종이 없다 — 비워 두면 화면에서 해당 구간을 생략한다
        evidence: [] as string[],
        missing: [] as string[],
        interpretation: '',
        // 장기도 2026-08-13 스펙부터 서버가 설명문을 준다
        explainSummary: o?.organSummary ?? '',
        explainDetail: o?.detailedAnalysis ?? '',
      };
    }
    return null;
  }, [twin, organKey, diseaseKey]);

  // === AI 위험도 분석 — 현재/예상 위험도 (아직 예시 값, 추후 서버 연동) ===
  // 위험이 높은 쪽을 빨강, 낮은 쪽을 초록으로 칠하고, 그에 맞춰 화살표 그라데이션·배지가
  // 자동으로 방향을 바꾼다. 예상이 더 낮아지면(개선) 빨강→초록, 더 높아지면 초록→빨강.
  const RISK_RED = '#ed5454';
  const RISK_GREEN = '#35AE17';
  const currentRisk = 76;
  const predictedRisk = 42;
  const riskImproving = predictedRisk <= currentRisk;       // 예상이 더 낮으면 개선
  const riskDiff = Math.abs(currentRisk - predictedRisk);
  const currentColor = riskImproving ? RISK_RED : RISK_GREEN;   // 개선이면 현재가 높은 쪽(빨강)
  const predictedColor = riskImproving ? RISK_GREEN : RISK_RED; // 개선이면 예상이 낮은 쪽(초록)
  const badgeColor = riskImproving ? RISK_GREEN : RISK_RED;
  const badgeBg = riskImproving ? '#e6f6e1' : '#fdeaea';
  const badgeIcon = riskImproving ? '▼' : '▲';

  return (
    <DigitalTwinShell>
      {/* 상단 타이틀 */}
      <Typography sx={{ mt: 2.5, color: '#2A4268', fontSize: 22, fontWeight: 900, textAlign: 'center' }}>
        예측 위험도{' '}
        <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: '#2A4268' }}>
          질환별 상세분석
        </Box>
      </Typography>

      {/* 질병 예측 카드 */}
      <Box sx={{ mx: 2, mt: 2.5 }}>
        <Typography sx={{ color: '#333', fontSize: 15, fontWeight: 700, mb: 1 }}>질병 예측</Typography>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: 2.5,
            boxShadow: '0 2px 8px rgba(197,211,218,.4)',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 140,
              height: 140,
              mx: 'auto',
              bgcolor: '#eef2f6',
              borderRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* 아이콘 — 장기/질환에 맞춰 바뀐다 (없으면 기본 이모지) */}
            {view?.icon ? (
              <Box
                component="img"
                src={view.icon}
                alt={view?.title ?? ''}
                sx={{ width: 60, height: 60, objectFit: 'contain', mb: 0.5 }}
              />
            ) : (
              <Box sx={{ fontSize: 38, color: '#54a4cb', mb: 0.5 }}>🩸</Box>
            )}
            {/*
              '발병위험도 %' 는 스펙에 없는 값이라 서버 score(위험 점수)를 그대로 쓴다.
              발병 확률처럼 보이지 않도록 단위를 '점'으로, 문구도 '위험 점수'로 둔다.
            */}
            <Typography sx={{ color: '#333', fontSize: 18, fontWeight: 900 }}>
              {view?.summary.score === null || view?.summary.score === undefined
                ? '미측정'
                : `${view.summary.score}점`}{' '}
              <Box
                component="span"
                sx={{ color: STATUS_TONE[view?.status ?? ''] ?? '#777', fontSize: 14, fontWeight: 700 }}
              >
                {view?.status ?? ''}
              </Box>
            </Typography>
          </Box>
          <Typography sx={{ mt: 2, color: '#333', fontSize: 15, fontWeight: 800 }}>
            <Box component="span" sx={{ color: STATUS_TONE[view?.status ?? ''] ?? '#333', fontSize: 18 }}>
              {view?.title ?? '대상'} 위험 점수
            </Box>{' '}
            입니다.
          </Typography>
        </Box>
      </Box>

      {/* 위험도 분석 결과 카드 — 질병 예측 다음 */}
      <Typography sx={{ mx: 2, mt: 3, mb: 1, color: '#333', fontSize: 15, fontWeight: 700 }}>
        위험도 분석
      </Typography>
      <SectionCard sx={{ mt: 0 }}>
        {/* 질환명 + 위험 Grade 뱃지 */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.6 }}>
          <Box>
            <Typography sx={{ color: '#888', fontSize: 14, fontWeight: 700 }}>
              {diseaseKey ? '질환명' : '장기'}
            </Typography>
            <Typography sx={{ color: '#2A4268', fontSize: 18, fontWeight: 900, mt: 0.2 }}>
              {view?.title ?? '미측정'}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.4,
              py: 0.6,
              borderRadius: '99px',
              bgcolor: '#f3f6fa',
              color: STATUS_TONE[view?.status ?? ''] ?? '#777',
              fontSize: 14,
              fontWeight: 800,
              maxWidth: '62%',
              textAlign: 'right',
              wordBreak: 'keep-all',
            }}
          >
            {/* 서버 riskGrade 원문을 그대로 보여준다 — 등급 문구를 임의로 바꾸지 않는다 */}
            {view?.grade ?? '미측정'}
          </Box>
        </Stack>

        {/* 신뢰도 / 데이터 충족률 */}
        <Stack direction="row" spacing={1.2} sx={{ mb: 1.8 }}>
          <Box sx={{ flex: 1, bgcolor: '#f3f6fa', borderRadius: '12px', p: 1.4 }}>
            <Typography sx={{ color: '#888', fontSize: 14, fontWeight: 700 }}>신뢰도</Typography>
            <Typography sx={{ color: '#35AE17', fontSize: 17, fontWeight: 900, mt: 0.3 }}>
              {view?.summary.confidence ?? '미측정'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, bgcolor: '#f3f6fa', borderRadius: '12px', p: 1.4 }}>
            <Typography sx={{ color: '#888', fontSize: 14, fontWeight: 700 }}>데이터 충족률</Typography>
            <Typography sx={{ color: '#2A4268', fontSize: 17, fontWeight: 900, mt: 0.3 }}>
              {view?.summary.coverage === null || view?.summary.coverage === undefined
                ? '미측정'
                : `${view.summary.coverage}%`}
            </Typography>
            <Box sx={{ mt: 0.7, height: 5, borderRadius: 99, bgcolor: '#e2e8ee', overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${view?.summary.coverage ?? 0}%`,
                  height: '100%',
                  bgcolor: '#54a4cb',
                  borderRadius: 99,
                }}
              />
            </Box>
          </Box>
        </Stack>

        {/* 근거 데이터 — AP_009_002 의 evidenceData (질환 응답에만 있다) */}
        {(view?.evidence.length ?? 0) > 0 && (
          <>
            <Typography sx={{ color: '#333', fontSize: 14, fontWeight: 800, mb: 0.8 }}>근거 데이터</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 1.8 }}>
              {view!.evidence.map((tag) => (
                <Box
                  key={tag}
                  sx={{
                    px: 1.1,
                    py: 0.4,
                    borderRadius: '8px',
                    bgcolor: '#e7f1f8',
                    color: '#3a78a8',
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* 부족 데이터 — AP_009_002 의 missingData */}
        {(view?.missing.length ?? 0) > 0 && (
          <>
            <Typography sx={{ color: '#333', fontSize: 14, fontWeight: 800, mb: 0.8 }}>부족 데이터</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 1.8 }}>
              {view!.missing.map((tag) => (
                <Box
                  key={tag}
                  sx={{
                    px: 1.1,
                    py: 0.4,
                    borderRadius: '8px',
                    bgcolor: '#f4f4f4',
                    color: '#9a9a9a',
                    fontSize: 14,
                    fontWeight: 700,
                    border: '1px dashed #d6d6d6',
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* 해석 — AP_009_002 의 interpretation */}
        {view?.interpretation ? (
          <Box sx={{ bgcolor: '#f3f6fa', borderRadius: '12px', p: 1.5 }}>
            <Typography sx={{ color: '#888', fontSize: 14, fontWeight: 800, mb: 0.5 }}>해석</Typography>
            <Typography sx={{ color: '#555', fontSize: 14, lineHeight: 1.6 }}>
              {view.interpretation}
            </Typography>
          </Box>
        ) : null}
      </SectionCard>

      {/* 설명 — 전부 서버(AP_009_002)가 이 사용자에 맞춰 생성한 문구다.
          질환은 diseaseSummary / detailedAnalysis, 장기는 organSummary / detailedAnalysis.
          예전에는 화면이 고정 문구를 들고 있었다(diseaseInfo.ts, 그 전에는 어떤 질환으로
          들어와도 당뇨병 설명 하나). 이제 값이 없으면 그 구간만 그리지 않는다 —
          운영 API 에 아직 배포되지 않았을 수 있으므로 빈 문자열을 그대로 두지 않는다. */}
      {(view?.explainSummary || view?.explainDetail) && (
        <React.Fragment>
          <Typography sx={{ mx: 2, mt: 3, mb: 1, color: '#333', fontSize: 15, fontWeight: 700 }}>
            {diseaseKey ? '질병 설명' : '장기 설명'}
          </Typography>
          {view?.explainSummary ? (
            <SectionCard sx={{ mt: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.9 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 16,
                    borderRadius: '99px',
                    bgcolor: '#54a4cb',
                  }}
                />
                <Typography sx={{ color: '#2A4268', fontSize: 15, fontWeight: 800 }}>현재 상태</Typography>
              </Stack>
              <Typography sx={{ color: '#555', fontSize: 14, lineHeight: 1.7, wordBreak: 'keep-all' }}>
                {view.explainSummary}
              </Typography>
            </SectionCard>
          ) : null}
          {view?.explainDetail ? (
            <SectionCard sx={{ mt: view?.explainSummary ? 1.5 : 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.9 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 16,
                    borderRadius: '99px',
                    bgcolor: '#54a4cb',
                  }}
                />
                <Typography sx={{ color: '#2A4268', fontSize: 15, fontWeight: 800 }}>상세 분석</Typography>
              </Stack>
              <Typography sx={{ color: '#555', fontSize: 14, lineHeight: 1.7, wordBreak: 'keep-all' }}>
                {view.explainDetail}
              </Typography>
            </SectionCard>
          ) : null}
        </React.Fragment>
      )}

      {/* 개선 액션 카드.
          제목은 서버 riskGrade 를 그대로 쓴다('미세 위험 - 혈압 관리 필요' 처럼 서버가
          문구를 만들어 준다). 예전에는 '내장지방 - 체지방률 개선 필요!' 가 빨간색으로
          고정돼 있어, 정상 판정이거나 간·뇌처럼 체지방과 무관한 대상을 보고 있어도
          체지방 경고가 떴다. 색도 판정에 맞춘다. */}
      <SectionCard sx={{ bgcolor: '#fff' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ color: STATUS_TONE[view?.status ?? ''] ?? '#333', fontSize: 15, fontWeight: 900 }}
            >
              {view?.grade || '판정 정보 없음'}
            </Typography>
            <Typography sx={{ color: '#555', fontSize: 14, mt: 0.5 }}>
              {view?.title ? `${view.title} 관리를 도와드릴게요` : '맞춤 관리를 도와드릴게요'}
            </Typography>
          </Box>
          <Box sx={{ fontSize: 26, color: '#7eb3d3', ml: 1 }}>🧬</Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {[
            // 음식·운동 추천은 케어의 맞춤식단·맞춤운동으로 보낸다 (맞춤플랜이 없으면 설정 화면).
            // AI 코칭은 아직 연결할 화면이 없어 그대로 둔다.
            { label: '음식 추천', bg: '#54a4cb', to: CARE_DIET_PATH },
            { label: '운동 추천', bg: '#54a4cb', to: CARE_SPORTS_PATH },
            { label: 'AI 코칭', bg: '#3a78a8', to: null },
          ].map((btn) => (
            <ButtonBase
              key={btn.label}
              onClick={btn.to ? () => goCarePlan(btn.to as string) : undefined}
              sx={{
                flex: 1,
                height: 36,
                borderRadius: '99px',
                bgcolor: btn.bg,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {btn.label}
            </ButtonBase>
          ))}
        </Stack>
      </SectionCard>

      {/* AI 위험도 분석 카드 */}
      <SectionCard sx={{ bgcolor: '#fff' }}>
        <Typography sx={{ color: '#333', fontSize: 15, fontWeight: 800, mb: 0.5 }}>AI 위험도 분석</Typography>
        <Typography sx={{ color: '#555', fontSize: 14, mb: 1.5 }}>
          스트레스·식단·운동 관리를 통한 심혈관 질환 개선 예상
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="space-around" sx={{ mb: 2 }}>
          {[
            { icon: iconAiStress, label: '스트레스 관리' },
            { icon: iconAiCholesterol, label: '콜레스테롤' },
            { icon: iconAiExercise, label: '운동' },
          ].map((item) => (
            <Box key={item.label} sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={item.icon}
                alt={item.label}
                sx={{ width: 50, height: 50, objectFit: 'contain' }}
              />
              <Typography sx={{ color: '#3D6494', fontSize: 14, fontWeight: 700, mt: 0.3 }}>{item.label}</Typography>
            </Box>
          ))}
        </Stack>
        {/* 하단: 3개월 후 위험도 개선 예측 (현재 → 예상) */}
        <Box sx={{ bgcolor: '#f4f7fb', borderRadius: '14px', p: 1.8 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {/* 현재 위험도 */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ color: '#9aa6b2', fontSize: 14, fontWeight: 700 }}>현재 위험도</Typography>
              <Typography sx={{ color: currentColor, fontSize: 25, fontWeight: 900, mt: 0.3, lineHeight: 1 }}>
                {currentRisk}%
              </Typography>
            </Box>

            {/* 중앙: 개선량 뱃지 + 화살표 + 기간 */}
            <Stack alignItems="center" spacing={0.5} sx={{ flexShrink: 0, px: 1 }}>
              <Box
                sx={{
                  px: 1.1,
                  py: 0.35,
                  borderRadius: '99px',
                  bgcolor: badgeBg,
                  color: badgeColor,
                  fontSize: 14,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.3,
                }}
              >
                <Box component="span" sx={{ fontSize: 14 }}>{badgeIcon}</Box> {riskDiff}%p
              </Box>
              {/* 체브론(»»») 화살표 — 현재(빨강)에서 개선(초록)으로 흐른다 */}
              <Box
                component="svg"
                viewBox="0 0 84 24"
                sx={{ width: 100, height: 28, display: 'block' }}
              >
                <defs>
                  {/* userSpaceOnUse — 체브론 각각이 아니라 전체 폭(0~84)에 걸쳐 하나로 이어지는 그라데이션 */}
                  <linearGradient
                    id="riskArrowGrad"
                    gradientUnits="userSpaceOnUse"
                    x1="6"
                    y1="0"
                    x2="79"
                    y2="0"
                  >
                    <stop offset="0%" stopColor={currentColor} />
                    <stop offset="50%" stopColor="#ff9a3d" />
                    <stop offset="100%" stopColor={predictedColor} />
                  </linearGradient>
                </defs>
                <g
                  fill="none"
                  stroke="url(#riskArrowGrad)"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 5 L15 12 L6 19" />
                  <path d="M22 5 L31 12 L22 19" />
                  <path d="M38 5 L47 12 L38 19" />
                  <path d="M54 5 L63 12 L54 19" />
                  <path d="M70 5 L79 12 L70 19" />
                </g>
              </Box>
              <Typography sx={{ color: '#9aa6b2', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                3개월 후
              </Typography>
            </Stack>

            {/* 예상 위험도 */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ color: '#9aa6b2', fontSize: 14, fontWeight: 700 }}>예상 위험도</Typography>
              <Typography sx={{ color: predictedColor, fontSize: 25, fontWeight: 900, mt: 0.3, lineHeight: 1 }}>
                {predictedRisk}%
              </Typography>
            </Box>
          </Stack>
        </Box>
      </SectionCard>
    </DigitalTwinShell>
  );
};

export default DigitalTwinScreen3;
